import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// ── Shared styles ────────────────────────────────────────────

export const labelSx = {
  fontSize: '9px',
  fontWeight: 600,
  color: 'text.secondary',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
  lineHeight: 1.2,
  mb: 0.25,
};

export const valueSx = { fontSize: '12px', lineHeight: 1.4 };

// ── Helper functions ─────────────────────────────────────────

export const formatAddress = (
  address?: {
    line1?: string;line2?: string; suburb?: string;
    city?: string; state?: string; postCode?: string;
  } | null,
): string => {
  if (!address) return '-';
  const parts = [
    address.line1, address.line2, address.suburb,
    address.city, address.state, address.postCode,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '-';
};

export const getContactPhone = (
  contactData?: Array<{ phone1?: string; phone2?: string; isActive?: boolean }> | null,
): string => {
  if (!contactData || contactData.length === 0) return '-';
  const active = contactData.find((c) => c.isActive !== false);
  return active?.phone1 || active?.phone2 || '-';
};

export const getContactName = (
  contactData?: Array<{ contactName?: string; isActive?: boolean }> | null,
): string => {
  if (!contactData || contactData.length === 0) return '-';
  const active = contactData.find((c) => c.isActive !== false);
  return active?.contactName || '-';
};

/** Address value object matching the BFF shape. */
export interface AddressValue {
  line1?: string;
  line2?: string;
  suburb?: string;
  city?: string;
  stateName?: string;
  postCode?: string;
  countryName?: string;
  countryId?: number;
  stateId?: number;
  googleAddress?: string;
  isManual?: boolean;
  addressType?: string;
  googleResponse?: unknown;
}

export const parseServiceAddress = (addr: unknown): AddressValue => {
  if (!addr || typeof addr !== 'object') return {};
  return addr as AddressValue;
};

/** Extract attribute value by propertyTypeCode and optional subType. */
export const findAttr = (
  attrs: Array<Record<string, unknown>>,
  typeCode: string,
  subType?: string,
): Record<string, unknown> | undefined => {
  return attrs.find((a) => {
    if (a.propertyTypeCode !== typeCode) return false;
    if (subType) {
      const val = a.value as Record<string, unknown> | undefined;
      return val?.addressType === subType || val?.contactType === subType;
    }
    return true;
  });
};

export const formatAttrAddress = (attr?: Record<string, unknown>): string => {
  if (!attr) return '-';
  const val = attr.value as Record<string, string> | undefined;
  if (!val) return '-';
  const parts = [val.line1, val.line2, val.suburb, val.city, val.state, val.postCode].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '-';
};

export const formatAttrContact = (
  attr?: Record<string, unknown>,
): { name: string; phone: string } => {
  if (!attr) return { name: '-', phone: '-' };
  const val = attr.value as Record<string, string> | undefined;
  return {
    name: val?.contactName ?? '-',
    phone: val?.phone1 ?? val?.phone2 ?? '-',
  };
};

export const extractReferences = (
  attrs: Array<Record<string, unknown>>,
): { ref1?: string; ref2?: string; ref3?: string } => {
  const refAttr = attrs.find((a) => a.propertyTypeCode === 'reference-numbers');
  if (!refAttr) return {};
  const val = refAttr.value as Record<string, string> | undefined;
  return {
    ref1: val?.referenceNo1,
    ref2: val?.referenceNo2,
    ref3: val?.referenceNo3,
  };
};

// ── Sub-components ───────────────────────────────────────────

export const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ mb: 1 }}>
    <Typography sx={labelSx}>{label}</Typography>
    <Typography sx={valueSx}>{value || '—'}</Typography>
  </Box>
);

/** Only renders if value is non-empty. */
export const OptionalField: React.FC<{ label: string; value?: unknown }> = ({ label, value }) => {
  if (value == null) return null;
  const str = String(value).trim();
  if (!str) return null;
  return <Field label={label} value={str} />;
};
